// OCR text rendering. PaddleOCR-VL emits markdown, so the document inspector's
// Text and Side-by-side views render it as HTML rather than as a flat string.
//
// Pipeline: markdown -> HTML (marked) -> sanitize (DOMPurify) -> best-effort
// search highlight. Highlighting runs AFTER rendering, on the sanitized HTML's
// text nodes only, so it never corrupts tags/attributes. A search term that
// straddles markdown formatting (e.g. split across a `**bold**` boundary) just
// won't highlight — we prefer clean, consistent output over exhaustive marking.

import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { escapeRegExp } from './highlight';

marked.setOptions({
    gfm: true,
    breaks: true,  // OCR line breaks are meaningful; treat single \n as <br>.
});

/** Render OCR markdown to sanitized HTML (no highlighting). */
export const renderMarkdown = (text: string): string => {
    // marked.parse is synchronous given our (non-async) options; cast for TS.
    const rawHtml = marked.parse(text) as string;
    return DOMPurify.sanitize(rawHtml);
};

/** Wrap case-insensitive matches of `query` in <mark>, operating ONLY on the
 *  text between HTML tags so the markup is never broken. Best-effort: matches
 *  spanning a tag boundary are left un-marked. */
const highlightHtml = (html: string, query: string): string => {
    const trimmed = query.trim();
    if (!trimmed) return html;
    const term = new RegExp(escapeRegExp(trimmed), 'ig');
    // Split into tags ("<...>") and the text between them; only touch the text.
    return html.replace(/<[^>]+>|[^<]+/g, segment => {
        if (segment.startsWith('<')) return segment;       // a tag — leave as-is
        return segment.replace(term, m => `<mark>${m}</mark>`);
    });
};

/** Render OCR markdown to sanitized HTML, then apply best-effort highlighting
 *  for `query`. Returns an HTML string for dangerouslySetInnerHTML. */
export const renderMarkdownWithHighlight = (
    text: string,
    query: string | null | undefined,
): string => {
    const html = renderMarkdown(text);
    if (!query || !query.trim()) return html;
    return highlightHtml(html, query);
};
